import React, { useState } from 'react';
import styled from 'styled-components';
import { Plus, Edit3, Trash2, RefreshCw, Wallet } from 'lucide-react';
import { backofficeApi, formatNumber, extractErrorMessage } from '@kardume/services';
import { useNotification } from '@kardume/hooks';
import type {
  PaymentMethodDto,
  PaymentMethodCreateRequestDto,
  PaymentMethodUpdateRequestDto,
} from '@kardume/types';
import {
  PageHeader,
  StatCard,
  Table,
  TableContainer,
  Badge,
  Button,
  ActionButton,
  SearchInput,
  Select,
  TextInput,
  Switch,
  Modal,
  Pagination,
} from '@kardume/ui';
import { useAsync } from '../utils/useAsync';
import { useDebouncedValue } from '../utils/useDebouncedValue';
import DataState from '../components/DataState';

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;
  margin-bottom: 1.25rem;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const Toolbar = styled.div`
  display: flex;
  gap: 0.75rem;
  margin-bottom: 1rem;
  flex-wrap: wrap;
  align-items: center;

  > *:first-child {
    flex: 1;
    min-width: 240px;
  }
`;

const RowActions = styled.div`
  display: flex;
  gap: 0.5rem;
  justify-content: flex-end;
`;

const NameCell = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;

  strong {
    font-size: 0.9rem;
    color: ${(p) => p.theme.textPrimary};
  }
`;

const CodeText = styled.code`
  font-size: 0.76rem;
  color: ${(p) => p.theme.textMuted};
`;

const FieldRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.75rem;
  margin-top: 0.75rem;
`;

const FieldHint = styled.p`
  margin: 0.45rem 0 0;
  font-size: 0.78rem;
  line-height: 1.45;
  color: ${(p) => p.theme.textMuted};
`;

const ToggleField = styled.label`
  display: flex;
  align-items: center;
  gap: 0.6rem;
  margin-top: 0.85rem;
  padding: 0.7rem 0.85rem;
  border: 1px solid ${(p) => p.theme.border};
  border-radius: ${(p) => p.theme.radius};
  background: ${(p) => p.theme.surfaceAlt};
  font-size: 0.85rem;
  font-weight: 600;
  color: ${(p) => p.theme.textSecondary};
  cursor: pointer;
`;

const ConfirmText = styled.p`
  margin: 0;
  font-size: 0.9rem;
  line-height: 1.5;
  color: ${(p) => p.theme.textSecondary};

  strong {
    color: ${(p) => p.theme.textPrimary};
  }
`;

const STATUS_FILTER = [
  { value: '', label: 'Todas as situações' },
  { value: 'active', label: 'Ativas' },
  { value: 'inactive', label: 'Inativas' },
];

const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];

interface PaymentMethodForm {
  code: string;
  name: string;
  icon: string;
  sortOrder: string;
  active: boolean;
}

const EMPTY_FORM: PaymentMethodForm = { code: '', name: '', icon: '', sortOrder: '0', active: true };

/**
 * O código é o que amarra a declaração já feita pelas lojas ao vocabulário - e
 * é também por ele que o app filtra a vitrine em memória. Por isso ele é
 * normalizado na digitação: o backend guarda em MAIÚSCULAS, e deixar a tela
 * mostrar "pix" enquanto o banco guarda "PIX" faria a operação achar que criou
 * dois.
 */
const normalizeCode = (value: string): string =>
  value
    .toUpperCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^A-Z0-9_]/g, '_')
    .replace(/_{2,}/g, '_');

/**
 * Catálogo de formas de pagamento (card 311).
 *
 * Fica em Plataforma, ao lado de Comodidades, porque é a mesma coisa: um
 * vocabulário da plataforma que se mexe de vez em quando e vale para todo
 * mundo. O que se cria aqui aparece no perfil do lojista e nos filtros da
 * vitrine na próxima abertura da tela - "vale-cultura" deixou de custar um
 * deploy.
 */
const PaymentMethodsPage: React.FC = () => {
  const { showNotification } = useNotification();
  const [search, setSearch] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [page, setPage] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(20);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [editing, setEditing] = useState<PaymentMethodDto | null>(null);
  const [form, setForm] = useState<PaymentMethodForm>(EMPTY_FORM);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [deleting, setDeleting] = useState<PaymentMethodDto | null>(null);
  const [removing, setRemoving] = useState<boolean>(false);

  const debouncedSearch = useDebouncedValue(search.trim());

  const { data, loading, error, reload } = useAsync(
    () =>
      backofficeApi.listPaymentMethods({
        page,
        size: pageSize,
        query: debouncedSearch || undefined,
        active: statusFilter === '' ? undefined : statusFilter === 'active',
        sort: 'sortOrder,asc',
      }),
    [page, pageSize, debouncedSearch, statusFilter]
  );

  const methods = data?.content ?? [];
  const totalItems = data?.totalElements ?? 0;
  const totalPages = data?.totalPages ?? 0;

  const resetToFirstPage = (): void => setPage(0);

  const openCreate = (): void => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setIsOpen(true);
  };

  const openEdit = (method: PaymentMethodDto): void => {
    setEditing(method);
    setForm({
      code: method.code,
      name: method.name,
      icon: method.icon ?? '',
      sortOrder: String(method.sortOrder),
      active: method.active,
    });
    setIsOpen(true);
  };

  const update = <K extends keyof PaymentMethodForm>(key: K, value: PaymentMethodForm[K]): void => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const submit = async (): Promise<void> => {
    if (!form.name.trim()) {
      showNotification('Informe o nome da forma de pagamento.', 'error');
      return;
    }
    if (!editing && !form.code.trim()) {
      showNotification('Informe o código da forma de pagamento.', 'error');
      return;
    }
    const sortOrder = Number(form.sortOrder);
    if (!Number.isFinite(sortOrder) || sortOrder < 0) {
      showNotification('A ordem de exibição deve ser um número igual ou maior que zero.', 'error');
      return;
    }

    setSubmitting(true);
    try {
      const icon = form.icon.trim() || null;
      if (editing) {
        const payload: PaymentMethodUpdateRequestDto = {
          name: form.name.trim(),
          icon,
          sortOrder,
          active: form.active,
        };
        await backofficeApi.updatePaymentMethod(editing.id, payload);
        showNotification('Forma de pagamento atualizada com sucesso.', 'success');
      } else {
        const payload: PaymentMethodCreateRequestDto = {
          code: normalizeCode(form.code),
          name: form.name.trim(),
          icon,
          sortOrder,
          active: form.active,
        };
        await backofficeApi.createPaymentMethod(payload);
        showNotification(
          'Forma de pagamento criada. Ela já aparece para seleção no painel do lojista.',
          'success'
        );
      }
      setIsOpen(false);
      reload();
    } catch (err) {
      showNotification(extractErrorMessage(err, 'Não foi possível salvar a forma de pagamento.'), 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const toggleActive = async (method: PaymentMethodDto): Promise<void> => {
    try {
      await backofficeApi.updatePaymentMethod(method.id, {
        name: method.name,
        icon: method.icon,
        sortOrder: method.sortOrder,
        active: !method.active,
      });
      showNotification(
        method.active
          ? 'Forma de pagamento desativada. As lojas que já a declararam continuam com ela.'
          : 'Forma de pagamento ativada.',
        'success'
      );
      reload();
    } catch (err) {
      showNotification(extractErrorMessage(err, 'Não foi possível alterar a situação.'), 'error');
    }
  };

  const confirmRemove = async (): Promise<void> => {
    if (!deleting) return;
    setRemoving(true);
    try {
      await backofficeApi.deletePaymentMethod(deleting.id);
      showNotification('Forma de pagamento excluída.', 'success');
      setDeleting(null);
      reload();
    } catch (err) {
      showNotification(extractErrorMessage(err, 'Não foi possível excluir a forma de pagamento.'), 'error');
    } finally {
      setRemoving(false);
    }
  };

  return (
    <>
      <PageHeader
        title="Formas de pagamento"
        subtitle="Como se paga no lugar: Pix, dinheiro, vale-refeição. As ativas aparecem para o lojista marcar no perfil e viram filtro na vitrine."
        actions={
          <Button variant="primary" onClick={openCreate}>
            <Plus size={16} /> Nova forma de pagamento
          </Button>
        }
      />

      <StatsGrid>
        <StatCard label="Total (respeita o filtro)" value={formatNumber(totalItems)} />
        <StatCard label="Ativas (nesta página)" value={formatNumber(methods.filter((m) => m.active).length)} />
        <StatCard label="Inativas (nesta página)" value={formatNumber(methods.filter((m) => !m.active).length)} />
      </StatsGrid>

      <Toolbar>
        <SearchInput
          placeholder="Buscar por nome ou código..."
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            resetToFirstPage();
          }}
        />
        <Select
          options={STATUS_FILTER}
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value);
            resetToFirstPage();
          }}
        />
        <ActionButton title="Atualizar" onClick={reload}>
          <RefreshCw size={16} />
        </ActionButton>
      </Toolbar>

      <DataState
        loading={loading}
        error={error}
        isEmpty={methods.length === 0}
        emptyTitle="Nenhuma forma de pagamento encontrada"
        emptyDescription="Cadastre a primeira forma de pagamento para o lojista poder declará-la no perfil da loja."
        onRetry={reload}
      >
        <TableContainer>
          <Table>
            <thead>
              <tr>
                <th>Forma de pagamento</th>
                <th style={{ width: '1%', whiteSpace: 'nowrap', textAlign: 'right' }}>Ordem</th>
                <th style={{ width: '1%', whiteSpace: 'nowrap', textAlign: 'right' }}>Lojas</th>
                <th style={{ width: '1%', whiteSpace: 'nowrap', textAlign: 'right' }}>Situação</th>
                <th style={{ width: '1%', whiteSpace: 'nowrap', textAlign: 'right' }}>Ações</th>
              </tr>
            </thead>
            <tbody>
              {methods.map((method) => (
                <tr key={method.id}>
                  <td>
                    <NameCell>
                      <Wallet size={14} />
                      <strong>{method.name}</strong>
                      <CodeText>{method.code}</CodeText>
                    </NameCell>
                  </td>
                  <td style={{ textAlign: 'right' }}>{formatNumber(method.sortOrder)}</td>
                  <td style={{ textAlign: 'right' }}>{formatNumber(method.merchantCount)}</td>
                  <td style={{ textAlign: 'right' }}>
                    <Badge tone={method.active ? 'success' : 'secondary'}>
                      {method.active ? 'Ativa' : 'Inativa'}
                    </Badge>
                  </td>
                  <td>
                    <RowActions>
                      <Switch
                        checked={method.active}
                        onChange={() => toggleActive(method)}
                        aria-label={method.active ? 'Desativar forma de pagamento' : 'Ativar forma de pagamento'}
                      />
                      <ActionButton
                        aria-label="Editar forma de pagamento"
                        title="Editar"
                        onClick={() => openEdit(method)}
                      >
                        <Edit3 size={16} />
                      </ActionButton>
                      <ActionButton
                        aria-label="Excluir forma de pagamento"
                        title={
                          method.merchantCount > 0
                            ? 'Declarada por lojas - desative em vez de excluir'
                            : 'Excluir'
                        }
                        color="#DC2626"
                        onClick={() => setDeleting(method)}
                      >
                        <Trash2 size={16} />
                      </ActionButton>
                    </RowActions>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </TableContainer>
      </DataState>

      {totalPages > 1 && (
        <Pagination
          currentPage={page + 1}
          totalPages={totalPages}
          totalItems={totalItems}
          pageSize={pageSize}
          pageSizeOptions={PAGE_SIZE_OPTIONS}
          disabled={loading}
          onPageChange={(next) => setPage(next - 1)}
          onPageSizeChange={(size) => {
            setPageSize(size);
            resetToFirstPage();
          }}
        />
      )}

      <Modal
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        title={editing ? 'Editar forma de pagamento' : 'Nova forma de pagamento'}
        width="460px"
        footer={
          <>
            <Button variant="ghost" onClick={() => setIsOpen(false)} disabled={submitting}>
              Cancelar
            </Button>
            <Button variant="primary" onClick={submit} isLoading={submitting}>
              Salvar
            </Button>
          </>
        }
      >
        <TextInput
          label="Nome"
          value={form.name}
          onChange={(e) => update('name', e.target.value)}
          placeholder="Ex.: Vale-cultura"
        />
        <TextInput
          label="Código"
          value={form.code}
          onChange={(e) => update('code', normalizeCode(e.target.value))}
          placeholder="Ex.: CULTURE_VOUCHER"
          disabled={editing !== null}
          containerStyle={{ marginTop: '0.75rem' }}
        />
        <FieldHint>
          {editing
            ? 'O código não muda depois de criado: é ele que amarra a declaração já feita pelas lojas e o filtro do aplicativo. Para corrigir o texto que aparece, use o nome.'
            : 'Identificador estável usado pelo site e pelo app. Depois de criado ele não pode mudar.'}
        </FieldHint>
        <FieldRow>
          <TextInput
            label="Ícone"
            value={form.icon}
            onChange={(e) => update('icon', e.target.value)}
            placeholder="Ex.: wallet"
          />
          <TextInput
            label="Ordem de exibição"
            type="number"
            min={0}
            value={form.sortOrder}
            onChange={(e) => update('sortOrder', e.target.value)}
          />
        </FieldRow>
        <FieldHint>
          A ordem define a sequência das etiquetas na página da loja e dos filtros da vitrine -
          menor primeiro.
        </FieldHint>

        <ToggleField>
          <Switch checked={form.active} onChange={(v) => update('active', v)} />
          Forma de pagamento ativa (oferecida ao lojista e usada como filtro)
        </ToggleField>
        <FieldHint>
          Desativar tira a forma das telas, mas <strong>não</strong> apaga a declaração das lojas que
          já a escolheram: reativá-la devolve a etiqueta a todas elas.
        </FieldHint>
      </Modal>

      <Modal
        isOpen={deleting !== null}
        onClose={() => setDeleting(null)}
        title="Excluir forma de pagamento"
        width="440px"
        footer={
          <>
            <Button variant="ghost" onClick={() => setDeleting(null)} disabled={removing}>
              Cancelar
            </Button>
            <Button
              variant="danger"
              onClick={confirmRemove}
              isLoading={removing}
              disabled={(deleting?.merchantCount ?? 0) > 0}
            >
              Excluir
            </Button>
          </>
        }
      >
        {deleting && deleting.merchantCount > 0 ? (
          <ConfirmText>
            <strong>{deleting.name}</strong> é declarada por {formatNumber(deleting.merchantCount)} loja(s) e
            por isso não pode ser excluída. Desative-a: ela sai das telas sem apagar a declaração de
            ninguém, e volta inteira se for reativada.
          </ConfirmText>
        ) : (
          <ConfirmText>
            Tem certeza que deseja excluir <strong>{deleting?.name ?? ''}</strong>? Ela deixará de ser
            oferecida ao lojista e de aparecer nos filtros da vitrine.
          </ConfirmText>
        )}
      </Modal>
    </>
  );
};

export default PaymentMethodsPage;
